import { API_URL } from '../../../constants/urls.constants';
import type { CommonResponse } from '../../../types';
import { APP_API_TAGS } from '../../app.constants';
import { appService } from '../../services/app-service';
import type {
  TAIInterviewParams,
  TAppliedJobsResponse,
  TApplyJobPayload,
  TApplyJobResponse,
  TCompleteInterviewData,
  TGetAIInterviewQuestionsResponse,
  TGetAllJobsParams,
  TGetAllOfferLettersParams,
  TGetAppliedJobsParams,
  TGetAssessmentInterviewParams,
  TGetAssessmentInterviewResponse,
  TGetPresignedUrlsResponse,
  TJobPostDetails,
  TJobPostsResponse,
  TOfferLetterDetails,
  TOfferLetterListResponse,
  TSubmitAssessmentPayload,
  TSubmitAssessmentResponse,
  TUpdateEmployeeProfilePayload,
  TUpdateOfferLetterStatusPayload,
} from './recruitment.types';

export const recruitmentApi = appService.injectEndpoints({
  endpoints: (builder) => ({
    getAllJobs: builder.query<
      CommonResponse<TJobPostsResponse>,
      TGetAllJobsParams | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();

        if (params) {
          Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
              queryParams.append(key, String(value));
            }
          });
        }

        return {
          url: `${API_URL.getAllJobs}?${queryParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: [APP_API_TAGS.JOBS],
    }),

    getSingleJob: builder.query<
      CommonResponse<TJobPostDetails>,
      { userId: string; id: string }
    >({
      query: ({ userId, id }) => ({
        url: `${API_URL.getSingleJob}/${id}`,
        method: 'GET',
        params: { applicant_id: userId },
      }),
      providesTags: (_result, _error, { id }) => [
        { type: APP_API_TAGS.JOB_DETAILS, id },
      ],
    }),

    getPublicCountries: builder.query<CommonResponse<string[]>, void>({
      query: () => ({
        url: API_URL.getPublicCountries,
        method: 'GET',
      }),
    }),

    getPublicIndustries: builder.query<CommonResponse<string[]>, void>({
      query: () => ({
        url: API_URL.getPublicIndustries,
        method: 'GET',
      }),
    }),

    applyJob: builder.mutation<
      CommonResponse<TApplyJobResponse>,
      TApplyJobPayload
    >({
      query: (payload) => ({
        url: API_URL.applyJob,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result) => {
        return result?.success
          ? [
              APP_API_TAGS.JOBS,
              APP_API_TAGS.APPLIED_JOBS,
              APP_API_TAGS.JOB_DETAILS,
            ]
          : [];
      },
    }),

    getAppliedJobs: builder.query<
      CommonResponse<TAppliedJobsResponse>,
      TGetAppliedJobsParams
    >({
      query: ({ applicant_id, ...params }) => {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            queryParams.append(key, String(value));
          }
        });

        return {
          url: `${API_URL.getAppliedJobs}/${applicant_id}?${queryParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: [APP_API_TAGS.APPLIED_JOBS],
    }),

    submitAssessment: builder.mutation<
      CommonResponse<TSubmitAssessmentResponse>,
      TSubmitAssessmentPayload
    >({
      query: ({ applicant_id, ...payload }) => ({
        url: `${API_URL.submitAssessment}/${applicant_id}/submit`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [APP_API_TAGS.APPLIED_JOBS],
    }),

    getAssessmentInterview: builder.query<
      CommonResponse<TGetAssessmentInterviewResponse>,
      TGetAssessmentInterviewParams
    >({
      query: ({ applicant_id, job_application_id }) => ({
        url: `${API_URL.getAssessmentInterview}/${applicant_id}/${job_application_id}`,
        method: 'GET',
      }),
      providesTags: (_, __, { job_application_id }) => [
        {
          type: APP_API_TAGS.APPLIED_JOBS,
          id: job_application_id,
        },
      ],
    }),

    getAllOfferLetters: builder.query<
      CommonResponse<TOfferLetterListResponse>,
      TGetAllOfferLettersParams
    >({
      query: ({ applicant_id, ...params }) => {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.append(key, String(value));
          }
        });
        return {
          url: `${API_URL.getAllOfferLetters}/${applicant_id}?${queryParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: [APP_API_TAGS.OFFER_LETTERS],
    }),

    getOfferLetterDetails: builder.query<
      CommonResponse<TOfferLetterDetails>,
      { applicant_id: string; offer_letter_id: string }
    >({
      query: ({ applicant_id, offer_letter_id }) => ({
        url: `${API_URL.getOfferLetterDetails}/${applicant_id}/${offer_letter_id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { offer_letter_id }) => [
        { type: APP_API_TAGS.OFFER_LETTERS, id: offer_letter_id },
      ],
    }),

    updateOfferLetterStatus: builder.mutation<
      CommonResponse<{ message: string }>,
      TUpdateOfferLetterStatusPayload
    >({
      query: ({ applicant_id, offer_letter_id, offer_letter_status }) => ({
        url: `${API_URL.updateOfferLetterStatus}/${applicant_id}/${offer_letter_id}/status`,
        method: 'PATCH',
        body: { offer_letter_status },
      }),
      invalidatesTags: (result) =>
        result?.success ? [APP_API_TAGS.OFFER_LETTERS] : [],
    }),

    updateEmployeeProfile: builder.mutation<
      CommonResponse<{ message: string; data: any }>,
      TUpdateEmployeeProfilePayload
    >({
      query: ({ applicant_id, ...payload }) => ({
        url: `${API_URL.updateEmployeeProfile}/${applicant_id}`,
        method: 'PATCH',
        body: payload,
      }),
    }),

    getAIInterviewQuestions: builder.query<
      CommonResponse<TGetAIInterviewQuestionsResponse>,
      TAIInterviewParams
    >({
      query: ({ applicant_id, ai_interview_id }) => ({
        url: `${API_URL.aiInterview}/${applicant_id}/${ai_interview_id}/questions`,
        method: 'GET',
      }),
    }),

    getPreassignedS3UrlToUploadAudio: builder.query<
      CommonResponse<TGetPresignedUrlsResponse>,
      TAIInterviewParams
    >({
      query: ({ applicant_id, ai_interview_id }) => ({
        url: `${API_URL.aiInterview}/${applicant_id}/${ai_interview_id}/presigned-url`,
        method: 'GET',
      }),
    }),

    completeInterview: builder.mutation<
      CommonResponse<{ message: string }>,
      {
        applicant_id: string;
        ai_interview_id: string;
        job_application_id: string;
        data: TCompleteInterviewData;
      }
    >({
      query: ({ applicant_id, ai_interview_id, data }) => ({
        url: `${API_URL.aiInterview}/${applicant_id}/${ai_interview_id}/upload-complete`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_, __, { job_application_id }) => [
        { type: APP_API_TAGS.APPLIED_JOBS, id: job_application_id },
      ],
    }),
  }),
});

export const {
  useGetAllJobsQuery,
  useGetSingleJobQuery,
  useGetPublicCountriesQuery,
  useGetPublicIndustriesQuery,
  useApplyJobMutation,
  useGetAppliedJobsQuery,
  useSubmitAssessmentMutation,
  useGetAssessmentInterviewQuery,
  useGetAllOfferLettersQuery,
  useGetOfferLetterDetailsQuery,
  useUpdateOfferLetterStatusMutation,
  useUpdateEmployeeProfileMutation,
  useGetAIInterviewQuestionsQuery,
  useGetPreassignedS3UrlToUploadAudioQuery,
  useCompleteInterviewMutation,
} = recruitmentApi;
